import { Location } from '@angular/common';
import {
  computed,
  effect,
  inject,
  Injectable,
  Signal,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { LoggingService } from '@nx-vigil/core/web/logging/data-access';
import { OAuthErrorEvent, OAuthService } from 'angular-oauth2-oidc';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  ReplaySubject,
} from 'rxjs';
import { getOrganization } from './organization-claims.adapter';
import {
  getPermissionClaims,
  type PermissionClaims,
} from './permission-claims.adapter';

export interface Identity {
  domain: string;
  clientId: string;
  audience: string;
  scopes?: string[];
  /**
   * When set, login uses a popup window instead of a full-page redirect.
   * Must be the URL of the static oauth-callback page served by this MFE
   * (e.g. `http://localhost:4201/oauth-callback.html`).
   *
   * angular-oauth2-oidc uses this value as `silentRefreshRedirectUri`, which it
   * passes as the `redirect_uri` when calling `initLoginFlowInPopup()`. The
   * callback page must post `location.href` back to `window.opener`.
   *
   * The host app never navigates away, making this approach host-agnostic and
   * compatible with web component embedding.
   */
  popupRedirectUri?: string;
}

export interface User {
  fullName?: string;
  email?: string;
  pictureUrl?: string;
  organization?: Organization;
  organizations?: string[];
}

export interface Organization {
  id: string;
  name: string;
  isTenant: boolean;
  logoUrl?: string;
}

interface UserProfile {
  info: {
    organizations?: string[];
  };
}

type Claims = Record<string, unknown>;

export function setupAuth(
  identity: Identity,
  authService: AuthService,
): () => Promise<void> {
  return async () => {
    await authService.configure(identity);
  };
}

export function hasPermissions(
  requiredPermissions: readonly string[],
): Signal<boolean> {
  const authService = inject(AuthService);

  return computed(() => authService.hasPermissions(requiredPermissions));
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly logger = inject(LoggingService);
  private readonly router = inject(Router);
  private readonly location = inject(Location);

  private readonly oauth = inject(OAuthService);
  private popupMode = false;
  private readonly permissionClaimAudience = signal<string | undefined>(
    undefined,
  );
  private readonly identityClaims = signal<Claims | undefined>(undefined);
  private readonly accessTokenClaims = signal<Claims | undefined>(undefined);
  private readonly permissionClaims = computed(() =>
    getPermissionClaims(
      (this.accessTokenClaims() ?? this.identityClaims()) as PermissionClaims,
      this.permissionClaimAudience(),
    ),
  );
  private readonly permissionChangesSubject$ = new ReplaySubject<
    readonly string[]
  >(1);
  private latestPermissionChange: readonly string[] = this.permissionClaims();
  private readonly _permissionChangeStream = effect(() => {
    const permissions = this.permissionClaims();

    if (arePermissionClaimsEqual(this.latestPermissionChange, permissions)) {
      return;
    }

    this.latestPermissionChange = permissions;
    this.permissionChangesSubject$.next(permissions);
  });
  private readonly isLoadedSubject$ = new BehaviorSubject<boolean>(false);
  public readonly isLoaded$ = this.isLoadedSubject$.asObservable();
  public readonly permissionsChanged$ =
    this.permissionChangesSubject$.asObservable();

  private readonly isAuthenticatedSubject$ = new BehaviorSubject<boolean>(
    false,
  );

  // Only emit authentication status after service is loaded
  public readonly isAuthenticated$ = combineLatest([
    this.isLoadedSubject$,
    this.isAuthenticatedSubject$,
  ]).pipe(
    filter(([isLoaded]) => isLoaded),
    map(([, isAuthenticated]) => isAuthenticated),
    distinctUntilChanged(),
  );

  private readonly userSubject$ = new BehaviorSubject<User | undefined>(
    undefined,
  );
  public readonly user$ = this.userSubject$.asObservable();

  private readonly loginErrorSubject$ = new BehaviorSubject<string | null>(
    null,
  );
  /** Emits a user-facing message when a popup login attempt fails; null otherwise. */
  public readonly loginError$ = this.loginErrorSubject$.asObservable();

  public constructor() {
    const logger = this.logger;

    this.oauth.setupAutomaticSilentRefresh({}, 'access_token');

    this.oauth.events.subscribe(event => {
      if (event instanceof OAuthErrorEvent) {
        logger.warn(
          'OAuthErrorEvent: type - {event.type}, reason - {event.reason}',
          event.type,
          event.reason,
        );
      }
      switch (event.type) {
        case 'token_received':
          this.loginErrorSubject$.next(null);
          this.refresh();
          break;
        case 'silent_refresh_error':
        case 'token_refresh_error':
        case 'session_error': // check_session iframe 'error' path — skips refreshToken so token_refresh_error is never emitted
          if (this.popupMode) {
            // In popup mode we cannot open a popup without a user gesture, so
            // auto-login here would be blocked. Clear state and let the user
            // click to re-authenticate.
            this.clearTokenClaims();
            this.userSubject$.next(undefined);
            this.isAuthenticatedSubject$.next(false);
            this.loginErrorSubject$.next('Your session has expired.');
          } else {
            this.login();
          }
          break;
        case 'logout':
          this.logout();
          break;
        case 'invalid_nonce_in_state':
          window.location.reload();
          break;
        default:
      }
    });
  }

  protected async refresh() {
    this.syncTokenClaims();
    this.userSubject$.next(await this.getIdentityClaims());
    this.isAuthenticatedSubject$.next(true);
  }

  private getRedirectedUrl(): string | undefined {
    // the login workflow involves a serialized state passed from our app through the oidc provider and back (queryString) - this is the how we reinstate the initial url
    if (this.oauth.state != null) {
      let stateUrl = this.oauth.state;
      if (stateUrl.startsWith('/') === false) {
        stateUrl = decodeURIComponent(stateUrl);
      }
      return stateUrl;
    }

    return undefined;
  }

  public isAuthorized() {
    return this.oauth.hasValidIdToken() && this.oauth.hasValidAccessToken();
  }

  public hasPermissions(requiredPermissions: readonly string[]): boolean {
    if (requiredPermissions.length === 0) {
      return true;
    }

    return requiredPermissions.every(permission =>
      this.permissionClaims().includes(permission),
    );
  }

  public login(target?: string): void {
    this.loginErrorSubject$.next(null);
    if (this.popupMode) {
      // Popup flow: the host page never navigates away. The library opens a
      // popup to the IdP, the callback page posts the URL back via postMessage,
      // and the library exchanges the code for tokens — all without leaving the
      // host. The token_received event fires and calls refresh() automatically.
      this.oauth
        .initLoginFlowInPopup({ width: 1000, height: 800 })
        .catch((err: OAuthErrorEvent) => {
          if (err?.type === 'popup_closed') {
            this.loginErrorSubject$.next('Login was cancelled.');
          } else if (err?.type === 'popup_blocked') {
            this.loginErrorSubject$.next(
              'The login popup was blocked by your browser. Please allow popups for this site and try again.',
            );
          } else {
            const message =
              err instanceof Error
                ? err.message
                : typeof err === 'object'
                  ? JSON.stringify(err)
                  : String(err);
            this.loginErrorSubject$.next(`Login failed: ${message}`);
          }
        });
    } else {
      this.logout();
      this.oauth.initLoginFlow(target ?? this.router.routerState.snapshot.url);
    }
  }

  public restartLogin(target?: string): void {
    this.oauth.initLoginFlow(target);
  }

  public logout(): void {
    if (this.isAuthorized()) {
      this.oauth.logOut();
      this.clearTokenClaims();
      this.userSubject$.next(undefined);
      this.isAuthenticatedSubject$.next(false);
      if (this.popupMode) {
        // In popup mode the component owns the auth state display. Signal that
        // the user was signed out so they see a prompt rather than LOADING.
        this.loginErrorSubject$.next('You have been signed out.');
      }
    }
  }

  public async getIdentityClaims(): Promise<User | undefined> {
    const claims = this.identityClaims();

    if (claims != null) {
      const userInfo = (await this.oauth.loadUserProfile()) as UserProfile;

      return {
        fullName: getStringClaim(claims, 'name'),
        email: getStringClaim(claims, 'email'),
        pictureUrl: getStringClaim(claims, 'picture'),
        organization: getOrganization(claims),
        organizations: userInfo?.info?.organizations,
      };
    }

    return undefined;
  }

  public async configure(identity: Identity): Promise<void> {
    this.popupMode = identity.popupRedirectUri != null;
    this.permissionClaimAudience.set(identity.audience);
    this.clearTokenClaims();

    this.oauth.configure({
      issuer: `https://${identity.domain}`,
      skipIssuerCheck: false,
      clientId: identity.clientId,
      responseType: 'code',
      redirectUri: window.location.origin,
      // silentRefreshRedirectUri doubles as the popup callback URI.
      // initLoginFlowInPopup() sends the IdP here; the static page at this
      // URL posts location.href back to window.opener so the library can
      // exchange the code without ever leaving the host page.
      ...(identity.popupRedirectUri
        ? { silentRefreshRedirectUri: identity.popupRedirectUri }
        : {}),
      scope: ['openid profile email roles', ...(identity.scopes ?? [])].join(
        ' ',
      ),
      useSilentRefresh: false,
      timeoutFactor: 0.8,
      // Disable session checks in popup mode. Both the host and the popup-mode
      // remote share the same Keycloak SSO session (same realm = same session
      // cookie). Every token refresh by either client rotates the SSO session
      // state hash on the server, which makes the other client's check_session
      // iframe report "changed" — triggering another refresh — creating a
      // feedback loop. Session expiry in popup mode is already handled by the
      // silent_refresh_error / token_refresh_error / session_error event paths.
      sessionChecksEnabled: !this.popupMode,
      showDebugInformation: false,
      clearHashAfterLogin: true,
      customQueryParams: {
        audience: identity.audience,
      },
      requireHttps: true,
    });

    await this.oauth.loadDiscoveryDocument();

    if (!this.popupMode) {
      await this.oauth.tryLogin();
    }

    if (this.oauth.hasValidAccessToken()) {
      await this.refresh();
      const url = this.getRedirectedUrl();
      if (url) {
        this.location.replaceState(url);
      }
    }

    this.isLoadedSubject$.next(true);
  }

  private syncTokenClaims(): void {
    if (!this.isAuthorized()) {
      this.clearTokenClaims();
      return;
    }

    this.identityClaims.set(
      (this.oauth.getIdentityClaims() as Claims | null) ?? undefined,
    );

    const accessToken = this.oauth.getAccessToken();

    this.accessTokenClaims.set(
      accessToken.length > 0 ? parseJwtClaims(accessToken) : undefined,
    );
  }

  private clearTokenClaims(): void {
    this.identityClaims.set(undefined);
    this.accessTokenClaims.set(undefined);
  }
}

function getStringClaim(
  claims: Claims | null | undefined,
  claimType: string,
): string | undefined {
  const value = claims?.[claimType];
  return typeof value === 'string' ? value : undefined;
}

function parseJwtClaims(token: string): Claims | undefined {
  const payload = token.split('.')[1];

  if (payload == null) {
    return undefined;
  }

  try {
    const normalizedPayload = normalizeBase64Url(payload);
    return JSON.parse(atob(normalizedPayload)) as Claims;
  } catch {
    return undefined;
  }
}

function normalizeBase64Url(value: string): string {
  const normalizedValue = value.replace(/-/g, '+').replace(/_/g, '/');
  const padding = normalizedValue.length % 4;

  if (padding === 0) {
    return normalizedValue;
  }

  return normalizedValue.padEnd(normalizedValue.length + (4 - padding), '=');
}

function arePermissionClaimsEqual(
  previous: readonly string[],
  current: readonly string[],
): boolean {
  if (previous.length !== current.length) {
    return false;
  }

  const currentSet = new Set(current);

  return previous.every(permission => currentSet.has(permission));
}
